import { ApiService } from './ApiService';
import { logger } from '../../utils/logger.js';
import DecryptionService from '../decryptionService';

/**
 * Interface pour l'envoi d'un message
 */
export interface SendMessageRequest {
  receiverId: string | number;
  content: string;
  messageType?: 'TEXT' | 'IMAGE' | 'FILE' | 'AUDIO' | 'VIDEO';
}

/**
 * Interface pour un message (correspond au backend)
 */
export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  messageType?: string;
  isRead: boolean;
  createdAt: string;
  updatedAt?: string;
  fileUrl?: string;
  sender?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role?: string;
  };
  receiver?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role?: string;
  };
}

/**
 * Interface pour une conversation (correspond au frontend)
 */
export interface Conversation {
  id: string;
  name: string;
  avatar?: string;
  lastMessage: string;
  lastTime: string;
  unread: number;
  participantType: 'doctor' | 'patient';
  participantId: string;
  isOnline: boolean;
}

interface RawConversation {
  id: string | number;
  name: string | Record<string, unknown>;
  avatar?: string;
  lastMessage: string | Record<string, unknown>;
  lastMessageDate?: string;
  unreadCount?: number;
  role?: string;
  isOnline?: boolean;
}

interface RawUser {
  id: string | number;
  name: string;
  email: string;
  role: string;
}

/**
 * Service de messagerie - Communication avec le backend
 */
class MessagingService {
  private api = ApiService.getInstance();

  // Le backend renvoie maintenant du camelCase strict grâce à la Phase 1 de refonte.
  
  /**
   * Obtenir toutes les conversations de l'utilisateur connecté
   * Le backend retourne directement les conversations structurées
   */
  async getConversations(): Promise<Conversation[]> {
    // Utiliser la normalisation de ApiService pour gérer les formats de réponse variés
    const response = await this.api.getNorm<RawConversation[]>('/api/messaging/conversations');
    const data = response.data || [];

    return data.map((conv) => {
      // Protection forte contre les objets renvoyés dans JSON (HDS Encryption artifacts)
      const name = typeof conv.name === 'object' ? JSON.stringify(conv.name) : String(conv.name || 'Utilisateur inconnu');
      const lastMessage = typeof conv.lastMessage === 'object' ? JSON.stringify(conv.lastMessage) : String(conv.lastMessage || '');
      
      let formattedTime = '';
      try {
        const d = new Date(conv.lastMessageDate || Date.now());
        formattedTime = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      } catch {
        formattedTime = '--:--';
      }

      return {
        id: String(conv.id),
        name: name,
        avatar: conv.avatar || undefined,
        lastMessage: lastMessage,
        lastTime: formattedTime,
        unread: Number(conv.unreadCount) || 0,
        participantType: (conv.role === 'DOCTOR' ? 'doctor' : 'patient') as 'doctor' | 'patient',
        participantId: String(conv.id),
        isOnline: Boolean(conv.isOnline),
      };
    });
  }

  /**
   * Méthode utilitaire pour obtenir l'ID de l'utilisateur actuel depuis le token
   * TODO: Implémenter une vraie récupération depuis le contexte d'authentification
   */
  private getCurrentUserId(): number | null {
    // Pour l'instant, retourner null - sera implémenté avec le contexte d'auth
    // Cette méthode devrait récupérer l'ID depuis le token JWT ou le contexte
    return null;
  }

  /**
   * Obtenir les messages d'une conversation spécifique
   * @param conversationId - ID de l'utilisateur avec qui on converse
   */
  async getMessages(conversationId: string | number): Promise<Message[]> {
    const response = await this.api.getNorm<Message[]>(
      `/api/messaging/conversation/${conversationId}`
    );
    
    const messages = response.data || [];
    
    return Promise.all(messages.map(async (msg) => ({
      ...msg,
      content: await DecryptionService.decrypt(msg.content)
    })));
  }

  /**
   * Envoyer un message
   */
  async sendMessage(data: SendMessageRequest): Promise<Message> {
    const response = await this.api.post<{ status: string; message: string; data: Message }>(
      '/api/messaging',
      data
    );
    return response.data;
  }

  /**
   * Marquer un message comme lu
   */
  async markAsRead(messageId: string | number): Promise<void> {
    await this.api.put<{ status: string; message: string }>(`/api/messaging/${messageId}/read`);
  }

  /**
   * Marquer toute une conversation comme lue
   */
  async markConversationAsRead(partnerId: string | number): Promise<void> {
    await this.api.put<{ status: string; message: string }>(
      `/api/messaging/conversation/${partnerId}/read`
    );
  }
  
  /**
   * Supprimer un message
   */
  async deleteMessage(messageId: string, _userId: string): Promise<void> {
    await this.api.delete(`/api/messaging/${messageId}`);
  }

  /**
   * Obtenir le nombre de messages non lus
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await this.api.getNorm<{ unreadCount: number }>(
        '/api/messaging/unread'
      );
      
      if (response.status === 'success' && response.data) {
        return Number(response.data.unreadCount) || 0;
      }
      return 0;
    } catch (error) {
      logger.error('Erreur récupération messages non lus', error);
      return 0;
    }
  }

  /**
   * Upload un fichier (image, audio, document)
   */
  async uploadFile(file: File): Promise<{ url: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);

    // ✅ Upload réel vers le backend
    const response = await this.api.post<{
      status: string;
      message: string;
      data: { url: string; fileName: string };
    }>('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Obtenir la liste des utilisateurs disponibles pour la messagerie
   * @param role - Filtrer par rôle (DOCTOR, PATIENT)
   */
  async getAvailableUsers(role?: string): Promise<
    Array<{
      id: string | number;
      name: string;
      email: string;
      role: string;
    }>
  > {
    // Si on cherche des docteurs, utiliser l'endpoint /doctors qui retourne TOUS les docteurs actifs
    if (role === 'DOCTOR') {
      const response = await this.api.getNorm<RawUser[]>('/api/users/doctors');
      return response.data || [];
    }

    // Pour les autres rôles, utiliser l'endpoint /online
    const params = role ? `?role=${role}` : '';
    const response = await this.api.getNorm<RawUser[]>(`/api/users/online${params}`);
    return response.data || [];
  }
}

export default new MessagingService();
